import { gql, useApolloClient, useQuery } from '@apollo/client';
import queryString from 'query-string';
import { useCallback, useContext } from 'react';
import { useRouter } from 'next/router';
import {
  ConversationList_GetConversationsDocument,
  ConversationList_GetConversationsQuery,
  Folder,
  ReadRepliedStatus,
  RenterProgressStatus,
} from '../types/GraphqlTypes';
import { InboxPageMobileView } from '../types/InboxPageMobileView';
import { InboxPageContext } from '../contexts/InboxPageContextProvider';
import useDevices from './useDevices';
import { MAX_OLD_CONVERSATION_DAYS } from '../constants/ConfigConstants';
import {
  inboxPageConversationIdToFilterByVariable,
  inboxPageConversationSearchTextVariable,
  inboxPageShowEmptyContainerVariable,
} from '../lib/apollo';

type ChangeConversationArgs = {
  conversationId: string;
  listingAlias: string;
  newMobileView?: InboxPageMobileView;
  replacePath?: boolean;
};

export type ChangeFiltersArgs = {
  folder?: Folder;
  renterProgressStatus?: RenterProgressStatus;
  byProperty?: boolean;
  readRepliedStatus?: ReadRepliedStatus;
  listingAlias?: string;
  daysBack?: number;
  mobileView?: InboxPageMobileView;
};

export default () => {
  const apolloClient = useApolloClient();
  const contextObj = useContext(InboxPageContext);
  const { isMobileView } = useDevices();
  const router = useRouter();
  const { data: inboxPageVariables } = useQuery(GET_INBOX_PAGE_VARIABLES, { fetchPolicy: 'cache-only' });
  const { inboxPageConversationIdToFilterBy } = inboxPageVariables;

  /**
   *
   * @param InboxPageContext data
   * @description Pass in a set of filters/folders, and this function will update the URL,
   * and also change to a new conversation within the new set of filters
   */
  const changeFiltersOrFolders = useCallback(
    async ({
      folder,
      renterProgressStatus,
      byProperty,
      readRepliedStatus,
      listingAlias,
    }: ChangeFiltersArgs): Promise<void> => {
      if (typeof byProperty === 'undefined') {
        byProperty = contextObj.isByProperty;
      }

      if (!listingAlias) {
        listingAlias = contextObj.listingAlias;
      }
      const newListingAlias = byProperty ? listingAlias : null;
      const filtersQueryString = getFiltersQueryString({
        daysBack: contextObj.daysBack,
        folder,
        renterProgressStatus,
        byProperty,
        readRepliedStatus,
      });

      // If you have a response from cache
      const response = apolloClient.cache.readQuery<ConversationList_GetConversationsQuery>({
        query: ConversationList_GetConversationsDocument,
        variables: {
          conversationId: inboxPageConversationIdToFilterBy || undefined,
          daysBack: contextObj.daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
          folder,
          listingAlias: byProperty ? listingAlias : null,
          renterProgressStatus,
          readRepliedStatus,
        },
      });

      if (response?.ZRM_getLatestConversationsV2.conversations.length) {
        const conversation = response?.ZRM_getLatestConversationsV2.conversations[0];

        updatePath({
          conversationId: conversation.conversationId,
          listingAlias: conversation.listingAlias,
          byProperty,
          filtersQueryString,
        });
      } else {
        updatePath({
          conversationId: null,
          listingAlias: byProperty || contextObj.isByProperty ? newListingAlias : null,
          byProperty,
          filtersQueryString,
        });
      }
    },
    [contextObj],
  );

  const resetFiltersAndFolders = useCallback(() => {
    inboxPageShowEmptyContainerVariable(false);
    inboxPageConversationSearchTextVariable('');
    inboxPageConversationIdToFilterByVariable('');

    const { conversationId, folder, listingAlias } = contextObj;
    const preserveConversationSlug = folder === Folder.Inbox && conversationId && listingAlias;

    router.push({
      pathname: router.pathname,
      query: preserveConversationSlug ? { conversationSlug: [listingAlias, conversationId] } : {},
    });
  }, [contextObj]);

  /**
   *
   * @param conversationId
   * @param listingAlias
   * @description Pass in a conversationId and listingAlias to go to. Pushes new URL into route history
   */
  const changeConversation = useCallback(
    async ({
      conversationId,
      listingAlias,
      newMobileView,
      replacePath = false,
    }: ChangeConversationArgs): Promise<void> => {
      if (isMobileView) {
        const mobileView = newMobileView || InboxPageMobileView.ConversationList;
        router.query.mobileView = mobileView;
      }

      const conversationSlug = [];
      if (listingAlias) {
        conversationSlug.push(listingAlias);
      }

      if (conversationId) {
        conversationSlug.push(conversationId);
      }

      const updatedRouter = {
        pathname: router.pathname,
        query: {
          ...router.query,
          conversationSlug,
        },
      };

      if (replacePath) {
        router.replace(updatedRouter);
      } else {
        router.push(updatedRouter);
      }
    },
    [isMobileView, router.query],
  );

  /**
   *
   * @description Pass in conversation page data to change URL
   */
  const updatePath = useCallback(
    ({
      conversationId,
      listingAlias,
      byProperty,
      filtersQueryString,
    }: {
      conversationId?: string | null;
      listingAlias?: string | null;
      filtersQueryString?: string;
      byProperty?: boolean;
    }): void => {
      const parsedQuery = queryString.parse(filtersQueryString);

      // Remove the byProperty query param if it's passed as false
      if (!byProperty && parsedQuery.byProperty) {
        delete parsedQuery.byProperty;
      }

      const conversationSlug = [];
      if (listingAlias) {
        conversationSlug.push(listingAlias);
      }

      if (conversationId) {
        conversationSlug.push(conversationId);
      }

      router.push({
        pathname: router.pathname,
        query: {
          ...parsedQuery,
          conversationSlug,
        },
      });
    },
    [contextObj],
  );

  const getFiltersQueryString = useCallback(
    ({
      daysBack,
      folder,
      renterProgressStatus,
      byProperty,
      readRepliedStatus,
      mobileView,
    }: ChangeFiltersArgs): string => {
      const paramsObj: Record<string, string> = {};

      if (folder && folder != Folder.Inbox) {
        if (folder === Folder.Spam) {
          paramsObj.folder = 'REPORTED';
        } else {
          paramsObj.folder = folder;
        }
      }

      if (readRepliedStatus && readRepliedStatus != ReadRepliedStatus.ShowAll) {
        paramsObj.readRepliedStatus = readRepliedStatus;
      }

      if (renterProgressStatus && renterProgressStatus != RenterProgressStatus.ShowAll) {
        paramsObj.renterProgressStatus = renterProgressStatus;
      }

      if (byProperty) {
        paramsObj.byProperty = 'true';
      }

      if (mobileView || isMobileView) {
        paramsObj.mobileView = mobileView || InboxPageMobileView.ConversationList;
      }

      if (daysBack) {
        paramsObj.daysBack = daysBack.toString();
      }

      if (Object.keys(paramsObj).length === 0) {
        return '';
      }

      return `?${queryString.stringify(paramsObj)}`;
    },
    [],
  );

  /**
   *
   * @param nextView of the mobileView to take user to next.
   *
   */
  const changeMobileView = useCallback(
    (nextView: InboxPageMobileView): void => {
      updatePath({
        byProperty: contextObj.isByProperty,
        conversationId: contextObj.conversationId,
        listingAlias: contextObj.listingAlias,
        filtersQueryString: getFiltersQueryString({
          daysBack: contextObj.daysBack,
          folder: contextObj.folder,
          readRepliedStatus: contextObj.readRepliedStatus,
          renterProgressStatus: contextObj.renterProgressStatus,
          byProperty: contextObj.isByProperty,
          mobileView: nextView,
        }),
      });
    },
    [contextObj],
  );

  /**
   *
   * @param queryParamToDelete
   * @param shouldReplaceHistory
   * @description Pass in the name of the queryParam to delete from the url. Replaces or pushes new URL into route history
   */
  const removeQueryParam = useCallback(
    (queryParamToDelete: string, shouldReplaceHistory = true) => {
      const updatedQuery = router.query;
      delete updatedQuery[queryParamToDelete];
      const updatedRouter = {
        pathname: router.pathname,
        query: updatedQuery,
      };

      return shouldReplaceHistory ? router.replace(updatedRouter) : router.push(updatedRouter);
    },
    [router.query],
  );

  return {
    changeMobileView,
    changeConversation,
    changeFiltersOrFolders,
    getFiltersQueryString,
    updatePath,
    removeQueryParam,
    resetFiltersAndFolders,
  };
};

const GET_INBOX_PAGE_VARIABLES = gql`
  query {
    inboxPageConversationIdToFilterBy @client
  }
`;
